// src/components/DashboardGroups/DashboardGroupColumn.tsx
import React, { useState, useRef, useCallback, useMemo, memo, useEffect } from 'react';
import RenderInBody from '@/components/utils/RenderInBody';
import { SwapVert, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ItemTypes } from '@/components/Dashboard/dndTypes';
import type { CardDragItem, ColumnDragItem } from '@/components/Dashboard/dndTypes';
import { setCardBeingDragged, getCardBeingDragged, registerCardElement, setGroupBeingDragged } from '@/lib/cardDragState';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ConversationItem } from '@/components/ConversationItem';
import type { DashboardGroupColumnProps, CardViewMode } from './types';
import type { ConversationDetail } from '@/types/conversation';
import { AddInlineTask } from './AddInlineTask';
import { DeleteDashboardGroupModal } from './modals/DeleteDashboardGroupModal';
import { ExpandedChildren } from './ExpandedChildren';
import { getAllDescendantIds } from './treeUtils';
import type { Dashboard } from '@/types/dashboard';
import type { SideMenuItem } from '@/services/menuService';
import MoveCopyToDashboardGroupModal from './modals/MoveCopyToDashboardGroupModal';
import { useAlertNotifications } from '@/contexts/AlertNotificationsContext';
import { useYesterdayMetrics } from '@/hooks/useYesterdayMetricsValidation';
import { getBookmarksConfigValueId } from '@/components/utils/CorgeeHelper';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import { apiService } from '@/services/api';

// Custom comparison function for DashboardGroupColumn
const areGroupPropsEqual = (
  prevProps: DashboardGroupColumnProps,
  nextProps: DashboardGroupColumnProps,
): boolean =>
{
  // Data — group object changes when Conversations are updated (handleMakeChild, handleCardDropped, etc.)
  if (prevProps.group !== nextProps.group) return false;
  // Hierarchy — must re-render when expanded set changes
  if (prevProps.expandedParentIds !== nextProps.expandedParentIds) return false;
  // Selection
  if (prevProps.selectedCardIds !== nextProps.selectedCardIds) return false;
  // View settings
  if (prevProps.cardViewMode !== nextProps.cardViewMode) return false;
  if (prevProps.columnWidth !== nextProps.columnWidth) return false;
  if (prevProps.isMultiDragActive !== nextProps.isMultiDragActive) return false;
  // Inline task
  if (prevProps.isAddingTask !== nextProps.isAddingTask) return false;
  // Pagination
  if (prevProps.totalItems !== nextProps.totalItems) return false;
  if (prevProps.hasMoreCards !== nextProps.hasMoreCards) return false;
  if (prevProps.isLoadingMore !== nextProps.isLoadingMore) return false;
  // Archived filter
  if (prevProps.hasArchivedFilter !== nextProps.hasArchivedFilter) return false;
  // Pending highlights
  if (prevProps.pendingGroupIds !== nextProps.pendingGroupIds) return false;
  if (prevProps.pendingConversationIds !== nextProps.pendingConversationIds) return false;
  // Check if the group object itself changed (e.g. name, ID)
  if (prevProps.group !== nextProps.group)
  {
    // If the reference changed, check if content is actually different
    if (
      prevProps.group.DashboardGroupId !== nextProps.group.DashboardGroupId ||
      prevProps.group.Name !== nextProps.group.Name ||
      prevProps.group.IsDefault !== nextProps.group.IsDefault ||
      prevProps.group.Conversations !== nextProps.group.Conversations // This is the simplified check; deep check is handled by virtualizer/cards
    )
    {
      return false;
    }
  }

  // Check primitive props
  if (
    prevProps.columnWidth !== nextProps.columnWidth ||
    prevProps.cardViewMode !== nextProps.cardViewMode ||
    prevProps.isMultiDragActive !== nextProps.isMultiDragActive ||
    prevProps.totalItems !== nextProps.totalItems ||
    prevProps.hideProjectName !== nextProps.hideProjectName ||
    prevProps.projectId !== nextProps.projectId ||
    prevProps.projectName !== nextProps.projectName
  )
  {
    return false;
  }

  // Check simple object references (config, api, etc usually stable or shouldn't trigger column re-render if deep contents change unless reference changes)
  if (
    prevProps.api !== nextProps.api ||
    prevProps.config !== nextProps.config ||
    prevProps.storageData !== nextProps.storageData ||
    prevProps.userData !== nextProps.userData ||
    prevProps.realItemTypes !== nextProps.realItemTypes ||
    prevProps.configMenus !== nextProps.configMenus ||
    prevProps.isAddingTask !== nextProps.isAddingTask
  )
  {
    return false;
  }

  if (
    prevProps.pendingGroupIds !== nextProps.pendingGroupIds ||
    prevProps.pendingConversationIds !== nextProps.pendingConversationIds
  )
  {
    return false;
  }

  if (
    prevProps.pendingGroupIds !== nextProps.pendingGroupIds ||
    prevProps.pendingConversationIds !== nextProps.pendingConversationIds
  )
  {
    return false;
  }

  // Check callbacks (should be stable via useCallback in parent, but strict check is fine)
  if (
    (prevProps.onCardCtrlClick !== nextProps.onCardCtrlClick ||
      prevProps.handleSelectAllCards !== nextProps.handleSelectAllCards ||
      prevProps.onColumnNameChange !== nextProps.onColumnNameChange ||
      prevProps.onOpenInfoWindow !== nextProps.onOpenInfoWindow ||
      prevProps.onUpdateConversation !== nextProps.onUpdateConversation ||
      prevProps.onUpdateDashboardGroupPosition !== nextProps.onUpdateDashboardGroupPosition ||
      prevProps.onEditConversation !== nextProps.onEditConversation,
    prevProps.onDuplicateItem !== nextProps.onDuplicateItem)
  )
  {
    return false;
  }

  if (
    prevProps.hasMoreCards !== nextProps.hasMoreCards ||
    prevProps.isLoadingMore !== nextProps.isLoadingMore
  )
  {
    return false;
  }

  // Check selectedCardIds set - deeper check needed?
  // If the Set reference changes, we need to know if any card IN THIS COLUMN is affected.
  // A simple reference check is "safe" (will re-render if set changes), but we can optimize.
  // For now, strict reference check on Set is usually what happens.
  // However, handleCardCtrlClick creates a new Set every time.
  // Optimization: Only re-render if a card in this column is selected or was selected.
  // But DashboardGroupColumn passes `selectedCardIds` down to SortableCard.
  if (prevProps.selectedCardIds !== nextProps.selectedCardIds)
  {
    return false;
  }

  return true;
};

export const DashboardGroupColumn = memo(function DashboardGroupColumn({
  group,
  dashboardId = 0,
  columnWidth,
  cardViewMode,
  index: _index, // unused but passed from parent
  selectedCardIds,
  isMultiDragActive,
  onCardCtrlClick,
  onCardShiftClick,
  handleSelectAllCards,
  onColumnNameChange,
  onAddNewTask,
  api,
  config,
  storageData,
  realItemTypes,
  configMenus,
  userData,
  onOpenInfoWindow,
  onEditConversation,
  onCreateConversation,
  onUpdateConversation,
  onDeleteConversation,
  onArchiveConversation,
  onRestoreConversation,
  onDeleteConversationPermanently,
  onDuplicateItem,
  onRemoveFromColumn,
  onBulkRemoveFromColumn,
  onBulkDeleteConversations,
  onConversationsProjectChanged,
  onChangedProject,
  onMoveFromColumn,
  onAddToColumn,
  recycleBin = false,
  totalItems: _totalItems,
  hideProjectName,
  isAddingTask,
  onCancelAddTask,
  onTaskAdded,
  configValueId,
  projectId,
  projectName,
  pendingGroupIds,
  pendingConversationIds,
  onUpdateDashboardGroupPosition,
  onLoadMoreCards,
  hasMoreCards,
  isLoadingMore = false,
  isOverlay,
  // Hierarchy props
  expandedParentIds,
  onToggleExpandParent,
  onNestableChange,
  onMakeChild,
  hierarchyDropActive,
  onCardDrop,
  onColumnDrop,
  onCallBackUpdateComplete,
  hasArchivedFilter = false,
  onInfoWindowCardDrop,
  onMakeDynamic,
  getSelectedConversations: getSelectedConversationsProp,
  socketConnectionId,
  onCreateItemFromCustomType,
}: DashboardGroupColumnProps)
{
  const navigate = useNavigate();
  const { unreadConversationIds, handleConversationViewed } = useAlertNotifications();
  const cardsDisabled = useYesterdayMetrics();
  const [isEditingName, setIsEditingName] = useState(false);
  // Delete Column
  const [openDeleteColumnModal, setOpenDeleteColumnModal] = useState(false);
  const [openMoveToColumnModal, setOpenMoveToColumnModal] = useState(false);
  const [openCopyToColumnModal, setOpenCopyToColumnModal] = useState(false);
  // Track if we should show "No More Items" message
  const [shouldShowNoMoreItems, setShouldShowNoMoreItems] = useState(false);
  const hasLoadedMoreRef = useRef(false);

  const [columnName, setColumnName] = useState(group.Name);
  const [baseName, setBaseName] = useState(group.Name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showAllDetails, setShowAllDetails] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const kebabBtnRef = useRef<HTMLButtonElement>(null);

  const hasItemCreationPermission = localStorage.getItem('C2_hasItemCreationPermission') === 'true';

  const rootCardCount = useMemo(
    () => group.Conversations?.filter(c => !c.ParentConversationId).length ?? 0,
    [group.Conversations],
  );

  // Stable refs so the fallback getSelectedConversations callback never changes identity
  const selectedCardIdsRef = useRef(selectedCardIds);
  selectedCardIdsRef.current = selectedCardIds;
  const groupConversationsRef = useRef(group.Conversations);
  groupConversationsRef.current = group.Conversations;
  const getSelectedConversationsLocal = useCallback((): ConversationDetail[] =>
  {
    return (
      groupConversationsRef.current?.filter(c =>
        selectedCardIdsRef.current.has(c.ConversationId),
      ) ?? []
    );
  }, []);
  const getSelectedConversations = getSelectedConversationsProp ?? getSelectedConversationsLocal;

  const handleAddLayerToCard = useCallback(
    async (
      convId: number,
      _e: React.MouseEvent | MouseEvent,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      const conv = groupConversationsRef.current?.find(c => c.ConversationId === convId);
      if (!conv) return;

      const alreadyPresent = (conv.ConversationLayers || []).some(
        (cl: any) => cl.LayerId === layer.LayerId,
      );

      if (alreadyPresent)
      {
        functions?.onUpdateLayerToCard?.(layer.LayerId);
        return;
      }

      const newLayerName = (layer as any).Name || layer.LayerName || '';
      const newLayerColor = (layer as any).Color || layer.LayerColor || '';
      const updatedLayers = [
        ...(conv.ConversationLayers || []),
        {
          ConversationId: conv.ConversationId,
          LayerId: layer.LayerId,
          LayerName: newLayerName,
          Name: newLayerName,
          LayerColor: newLayerColor,
          Color: newLayerColor,
          isForAdd: true,
        },
      ];

      try
      {
        const response = await apiService.put<any[]>(
          'conversation/UpdateLayers',
          { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketConnectionId },
        );

        const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
        const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
        const finalLayers = rawLayers.map((l: any) =>
        {
          if (l.Name || l.LayerName) return l;
          const src = existingLayerMap.get(l.LayerId);
          if (src)
          {
            return {
              ...l,
              Name: src.Name || src.LayerName || '',
              LayerName: src.LayerName || src.Name || '',
              LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
              Color: l.Color || src.Color || src.LayerColor || '',
            };
          }
          if (l.LayerId === layer.LayerId)
          {
            return { ...l, Name: newLayerName, LayerName: newLayerName, LayerColor: newLayerColor, Color: newLayerColor };
          }
          return l;
        });

        onUpdateConversation?.(convId, { ConversationLayers: finalLayers, isAddLayerToCard: true } as any);
        functions?.onUpdateLayerToCard?.(layer.LayerId);
      }
      catch (err)
      {
        console.error('Failed to add layer to card:', err);
      }
    },
    [onUpdateConversation],
  );

  const handleAddLayerToProject = useCallback(
    async (
      convId: number,
      _e: React.MouseEvent | MouseEvent,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void; skipCardUpdate?: boolean },
    ) =>
    {
      const conv = groupConversationsRef.current?.find(c => c.ConversationId === convId);
      if (!conv) return;

      const layerName = (layer as any).Name || layer.LayerName || '';
      const layerColor = (layer as any).Color || layer.LayerColor || '';
      let projectConversationId = (layer as any).ProjectConversationId;

      if (!projectConversationId && conv.ProjectId)
      {
        try
        {
          const proj = await apiService.get<any>(
            `Project/GetProject?projectId=${conv.ProjectId}`,
          );
          projectConversationId = proj?.ConversationId;
        }
        catch (_) { /* best-effort */ }
      }

      // Step 1: add layer to project conversation
      if (projectConversationId)
      {
        try
        {
          const projectConv = await apiService.post<any>(
            `conversation/GetConversationById?conversationId=${projectConversationId}`,
            null,
          );
          if (projectConv && projectConv.ConfigValueId !== 15)
          {
            let groups: any[] = [];
            try
            {
              const fetchedGroups = await apiService.get<any[]>(
                `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
              );
              if (Array.isArray(fetchedGroups)) groups = fetchedGroups;
            }
            catch (_) { /* best-effort */ }

            await apiService.post<any>('conversation/updateconversation', {
              ...projectConv,
              CustomFields: [],
              ConversationGroups: groups.length > 0 ? groups : projectConv.ConversationGroups,
              ConversationLayers: [
                ...(projectConv.ConversationLayers || []),
                {
                  ConversationId: projectConv.ConversationId,
                  LayerId: layer.LayerId,
                  LayerName: layerName,
                  LayerColor: layerColor,
                  isForAdd: true,
                  IsActive: 1,
                },
              ],
              SocketConnectionId: socketConnectionId,
            });
          }
        }
        catch (err)
        {
          console.error('Failed to add layer to project conversation:', err);
        }
      }

      if (functions?.skipCardUpdate)
      {
        functions.onUpdateLayerToCard?.(layer.LayerId);
        return;
      }

      // Step 2: add layer to current conversation
      try
      {
        const alreadyPresent = (conv.ConversationLayers || []).some(
          (cl: any) => cl.LayerId === layer.LayerId,
        );

        if (!alreadyPresent)
        {
          const updatedLayers = [
            ...(conv.ConversationLayers || []),
            {
              ConversationId: conv.ConversationId,
              LayerId: layer.LayerId,
              LayerName: layerName,
              Name: layerName,
              LayerColor: layerColor,
              Color: layerColor,
              isForAdd: true,
            },
          ];

          const response = await apiService.put<any[]>(
            'conversation/UpdateLayers',
            { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketConnectionId },
          );

          const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
          const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
          const finalLayers = rawLayers.map((l: any) =>
          {
            if (l.Name || l.LayerName) return l;
            const src = existingLayerMap.get(l.LayerId);
            if (src)
            {
              return {
                ...l,
                Name: src.Name || src.LayerName || '',
                LayerName: src.LayerName || src.Name || '',
                LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
                Color: l.Color || src.Color || src.LayerColor || '',
              };
            }
            if (l.LayerId === layer.LayerId)
            {
              return { ...l, Name: layerName, LayerName: layerName, LayerColor: layerColor, Color: layerColor };
            }
            return l;
          });

          onUpdateConversation?.(convId, { ConversationLayers: finalLayers, isAddLayerToCard: true } as any);
        }

        functions?.onUpdateLayerToCard?.(layer.LayerId);
      }
      catch (err)
      {
        console.error('Failed to add layer to current conversation:', err);
      }
    },
    [onUpdateConversation],
  );

  // Menu close is handled by the backdrop rendered via RenderInBody

  // react-dnd: Column drag (for reordering)
  const columnRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag<ColumnDragItem, void, { isDragging: boolean }>({
    type: ItemTypes.COLUMN,
    item: () =>
    {
      setGroupBeingDragged({
        groupId: group.DashboardGroupId,
        dashboardId: dashboardId ?? 0,
        isDefault: group.IsDefault ?? false,
        groupName: group.Name,
      });
      window.dispatchEvent(new CustomEvent('corgee:group-drag-start'));
      return { type: ItemTypes.COLUMN, id: group.DashboardGroupId, index: _index };
    },
    end: () =>
    {
      setGroupBeingDragged(null);
      window.dispatchEvent(new CustomEvent('corgee:group-drag-end'));
    },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => !isOverlay && !recycleBin,
  });

  const [{ isOverColumn }, dropColumn] = useDrop<ColumnDragItem, void, { isOverColumn: boolean }>({
    accept: ItemTypes.COLUMN,
    collect: monitor => ({ isOverColumn: monitor.isOver({ shallow: true }) }),
    canDrop: () => !recycleBin,
    drop: item =>
    {
      if (item.index !== _index) onColumnDrop?.(item.index, _index);
    },
  });

  const [{ isOverCards }, dropCards] = useDrop<CardDragItem, void, { isOverCards: boolean }>({
    accept: ItemTypes.CARD,
    collect: monitor => ({ isOverCards: monitor.isOver({ shallow: true }) }),
    canDrop: () => !recycleBin,
    drop: (item, monitor) =>
    {
      if (monitor.didDrop()) return; // already handled by a card
      onCardDrop?.(item.id, item.columnId, group.DashboardGroupId, null);
    },
  });

  dropColumn(columnRef);
  drag(headerRef);

  // Native HTML5 drag handlers for InfoWindow card drops (react-dnd doesn't see InfoWindow drags)
  const [isInfoWindowDragOver, setIsInfoWindowDragOver] = useState(false);

  const handleNativeDragOver = useCallback((e: React.DragEvent) =>
  {
    const dragged = getCardBeingDragged();
    if (!dragged || dragged.groupId !== 0 || !dragged.conversation) return; // only InfoWindow drags (groupId === 0)

    // 1. Validation for ProjectDetails:
    if (projectId && projectId !== '0')
    {
      const currentProjId = Number(projectId);
      const draggedProjId = dragged.conversation.ProjectId;
      if (draggedProjId !== currentProjId)
      {
        return;
      }
    }

    // 2. Validation for CardType or Projects:
    if (configValueId !== undefined && configValueId !== null && configValueId !== 1 && configValueId !== getBookmarksConfigValueId())
    {
      const draggedConfigId = dragged.conversation.ConfigValueId;
      if (draggedConfigId !== configValueId)
      {
        return;
      }
    }

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsInfoWindowDragOver(true);
  }, [projectId, configValueId]);

  const handleNativeDragLeave = useCallback((e: React.DragEvent) =>
  {
    // Only clear if leaving the column entirely (not entering a child element)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsInfoWindowDragOver(false);
  }, []);

  const handleNativeDrop = useCallback(
    (e: React.DragEvent) =>
    {
      setIsInfoWindowDragOver(false);
      const dragged = getCardBeingDragged();
      // Only intercept InfoWindow drags (groupId === 0); regular react-dnd card drags
      // must NOT call stopPropagation here because react-dnd listens at document level —
      // stopping propagation would prevent it from ever seeing the drop event.
      if (!dragged || dragged.groupId !== 0 || !dragged.conversation) return;

      // 1. Validation for ProjectDetails:
      if (projectId && projectId !== '0')
      {
        const currentProjId = Number(projectId);
        const draggedProjId = dragged.conversation.ProjectId;
        if (draggedProjId !== currentProjId)
        {
          return;
        }
      }

      // 2. Validation for CardType or Projects:
      if (configValueId !== undefined && configValueId !== null && configValueId !== 1 && configValueId !== getBookmarksConfigValueId())
      {
        const draggedConfigId = dragged.conversation.ConfigValueId;
        if (draggedConfigId !== configValueId)
        {
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();
      onInfoWindowCardDrop?.(dragged.conversation, group.DashboardGroupId);
    },
    [group.DashboardGroupId, onInfoWindowCardDrop, projectId, configValueId],
  );

  const style: React.CSSProperties = {
    ...(isDragging && !isOverlay ? { opacity: 0.5 } : {}),
    ...(isOverlay ? { cursor: 'grabbing', zIndex: 9999 } : {}),
    ...(isOverColumn ? { outline: '2px solid #62aacb' } : {}),
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      e.preventDefault();
      saveName();
    }
    if (e.key === 'Escape')
    {
      resetName();
    }
  };

  const resetName = () =>
  {
    setColumnName(baseName);
    setIsEditingName(false);
  };

  const saveName = () =>
  {
    setIsEditingName(false);
    if (columnName === baseName) return;
    setBaseName(columnName);
    if (onColumnNameChange)
    {
      onColumnNameChange(group.DashboardGroupId, columnName);
    }
  };

  // Menu action handlers
  const handleMenuAction = useCallback(
    (action: string) =>
    {
      setMenuOpen(false);
      switch (action)
      {
        case 'edit':
          setIsEditingName(true);
          break;
        case 'delete':
          setOpenDeleteColumnModal(true);
          break;
        case 'moveTo':
          // TODO: implement move to
          setOpenMoveToColumnModal(true);
          break;
        case 'copyTo':
          setOpenCopyToColumnModal(true);
          break;
        case 'makeDynamic':
          if (onMakeDynamic) onMakeDynamic(group.DashboardGroupId, group.DashboardId);
          break;
        case 'showAllDetails':
          setShowAllDetails(prev => !prev);
          break;
        case 'selectAll':
          handleSelectAllCards(groupConversationsRef.current.map(c => c.ConversationId));
          break;
      }
    },
    [group.DashboardGroupId],
  );

  // Build request object for ConversationDetailsView (BACKUP - not currently used)
  // const buildDetailRequest = useCallback(
  //   (conversation: ConversationDetail) => ({
  //     api: api,
  //     enableLoading: false,
  //     showErrorMessage: true,
  //     isInfoWindow: false,
  //     isInfowindowViewCard: false,
  //     hasArchivedFilter: false,
  //     isConversationDetailsView: true,
  //     hideCustomFields: true,
  //     config: config,
  //     userData: userData,
  //     storageData: storageData,
  //     params: {
  //       ConversationId: conversation.ConversationId,
  //       SocketConnectionId: undefined,
  //       IsFromProject: false,
  //       ConfigMenus: configMenus,
  //       ShowRefreshButton: true,
  //     },
  //     functions: {
  //       onLoadDataCallback: (conv: any) => {
  //         console.log('onLoadDataCallback:', conv);
  //       },
  //       onSendError: (convId: number) => {
  //         console.log('onSendError:', convId);
  //       },
  //       loadingBarStart: () => {},
  //       loadingBarComplete: () => {},
  //       openInfoWindow: (conv: any) => {
  //         if (onOpenInfoWindow) onOpenInfoWindow(conv);
  //       },
  //       attachReactFunctions: () => {},
  //       onUpdateConversation: (
  //         convId: number,
  //         newAttrs: any,
  //         _convObj: any,
  //         _renderType: any
  //       ) => {
  //         if (onUpdateConversation) onUpdateConversation(convId, newAttrs);
  //       },
  //       onAddReply: () => {},
  //       onUpdateReply: () => {},
  //       onDeleteReply: (_e: any, callback: any) => {
  //         if (callback) callback();
  //       },
  //       onCompleteToggle: () => {},
  //       markRepost: () => {},
  //       markFavorite: () => {},
  //       markRedflag: () => {},
  //       editReply: () => {},
  //       deleteReply: () => {},
  //       archiveConversation: (_e: any, _conversation: any, callback: any) => {
  //         if (callback) callback();
  //       },
  //       destroy: () => {},
  //       saveSearchResult: () => {},
  //       copyTo: () => {},
  //       onReplyDeleted: () => {},
  //       onCreateItemFromCustomType: () => {},
  //       onItemConvertTo: () => {},
  //       onChangedProject: () => {},
  //       onChangedSecurityGroups: () => {},
  //       onChangedGroupLayers: () => {},
  //       onChangeLayers: () => {},
  //       onCloseInfoWindow: () => {},
  //       getReloadItemFn: () => {},
  //       attachSocket: () => {},
  //     },
  //     realItemTypes: realItemTypes,
  //     conversation: conversation,
  //   }),
  //   [api, config, userData, storageData, configMenus, realItemTypes, onOpenInfoWindow, onUpdateConversation]
  // );

  // Build props for ConversationItem with isViewCard: true (detailed view)
  const buildViewCardProps = useCallback(
    (conversation: ConversationDetail, idx: number) => ({
      index: idx,
      conversation: conversation,
      api: api,
      isViewCard: true, // This is the key difference for detailed view
      forceShowDetails: showAllDetails,
      hasArchivedFilter: hasArchivedFilter,
      isConversationDetailsView: false,
      storageData: storageData,
      configMenus: configMenus,
      config: config,
      realItemTypes: realItemTypes,
      userData: userData,
      columnWidth: columnWidth,
      hideProjectName: hideProjectName,
      HasItemCreationPermission: hasItemCreationPermission,
      ShowMoveto: true,
      groupIsDefault: group.IsDefault,
      SocketConnectionId: socketConnectionId,
      corgeeFunctions: {
        onSendError: () => {},
        loadingBarStart: () => {},
        loadingBarComplete: () => {},
        callBackUpdateComplete: (response: any) =>
        {
          if (onCallBackUpdateComplete) onCallBackUpdateComplete(response);
        },
        openInfoWindow: (conv: ConversationDetail) =>
        {
          handleConversationViewed(conv.ConversationId);
          if (onOpenInfoWindow) onOpenInfoWindow(conv);
        },
        onCloseInfoWindow: (conv: ConversationDetail) =>
        {
          if (onOpenInfoWindow) onOpenInfoWindow(conv);
        },
        editConversation: (e: any, conv: ConversationDetail) =>
        {
          if (onEditConversation) onEditConversation(e, conv);
        },
        createConversation: (conv: ConversationDetail, cardType: SideMenuItem) =>
        {
          if (onCreateConversation) onCreateConversation(conv, cardType);
        },
        deleteConversation: (_e: any, conv: ConversationDetail) =>
        {
          if (onDeleteConversation) onDeleteConversation(conv.ConversationId);
        },
        archiveConversation: (_e: any, conv: ConversationDetail) =>
        {
          if (onArchiveConversation) onArchiveConversation(conv.ConversationId);
        },
        restoreConversation: (conv: ConversationDetail) =>
        {
          if (onRestoreConversation) onRestoreConversation(conv.ConversationId);
        },
        deleteConversationPermanently: (conv: ConversationDetail) =>
        {
          if (onDeleteConversationPermanently) onDeleteConversationPermanently(conv.ConversationId);
        },
        onDuplicateItem: (e: any, conv: ConversationDetail) =>
        {
          if (onDuplicateItem) onDuplicateItem(e, conv);
        },
        onRemoveFromColumn: (e: any, conv: ConversationDetail) =>
        {
          if (onRemoveFromColumn) onRemoveFromColumn(e, conv);
        },
        onBulkRemoveFromColumn: (conversations: ConversationDetail[]) =>
        {
          if (onBulkRemoveFromColumn) onBulkRemoveFromColumn(conversations);
        },
        bulkDeleteConversations: (conversationIds: number[]) =>
        {
          if (onBulkDeleteConversations) onBulkDeleteConversations(conversationIds);
        },
        onConversationsProjectChanged: (
          changes: Array<{ ConversationId: number; ProjectId: number; ProjectName: string }>,
        ) =>
        {
          if (onConversationsProjectChanged) onConversationsProjectChanged(changes);
        },
        onChangedProject: (_eOrId: any, conv?: any) =>
        {
          // CustomFields calls this as onChangedProject(undefined, conversationObj)
          // so ConversationId lives in the second argument, not the first.
          const conversationId = typeof _eOrId === 'number' ? _eOrId : conv?.ConversationId;
          if (onChangedProject && conversationId) onChangedProject(conversationId);
        },
        onMoveFromColumn: (
          e: any,
          conv: ConversationDetail,
          newGroupId: number,
          _response?: Dashboard,
        ) =>
        {
          if (onMoveFromColumn) onMoveFromColumn(e, conv, newGroupId);
        },
        onCreateItemFromCustomType: (item: any, conv: ConversationDetail) =>
        {
          if (onCreateItemFromCustomType) onCreateItemFromCustomType(item, conv);
        },
        addToColumn: (conv: ConversationDetail, targetGroupId: number) =>
        {
          if (onAddToColumn) onAddToColumn(conv, targetGroupId);
        },
        goToProject: (e: any, conv: ConversationDetail) =>
        {
          const path = `/ProjectDetails/${conv.ProjectId}`;
          if (e && (e.shiftKey || e.ctrlKey || e.metaKey))
          {
            window.open(window.location.origin + path, '_blank');
          }
          else
          {
            navigate(path);
          }
        },
      },
      onUpdateConversation: (convId: number, updates: Partial<ConversationDetail>) =>
      {
        if (onUpdateConversation) onUpdateConversation(convId, updates);
      },
      onUpdateReply: (convId: number, replyId: number, updates: Partial<any>) =>
      {
        if (!onUpdateConversation) return;
        const updatedReplies = (conversation.Replies || []).map((r: any) =>
          r.ReplyId === replyId ? { ...r, ...updates } : r,
        );
        onUpdateConversation(convId, { Replies: updatedReplies } as any);
      },
      onAddLayerToCard: (e: any, layer: any, functions?: any) =>
        handleAddLayerToCard(conversation.ConversationId, e, layer, functions),
      onAddLayerToProject: (e: any, layer: any, functions?: any) =>
        handleAddLayerToProject(conversation.ConversationId, e, layer, functions),
    }),
    [
      api,
      config,
      userData,
      storageData,
      configMenus,
      realItemTypes,
      onOpenInfoWindow,
      onUpdateConversation,
      onDeleteConversation,
      onArchiveConversation,
      onRestoreConversation,
      onDeleteConversationPermanently,
      hideProjectName,
      hasItemCreationPermission,
      showAllDetails,
      onEditConversation,
      onCreateConversation,
      onRemoveFromColumn,
      onBulkRemoveFromColumn,
      onBulkDeleteConversations,
      onConversationsProjectChanged,
      onChangedProject,
      onAddToColumn,
      onCallBackUpdateComplete,
      handleConversationViewed,
      socketConnectionId,
      handleAddLayerToCard,
      handleAddLayerToProject,
      onCreateItemFromCustomType,
    ],
  );

  // Build props for ConversationItem (compact view)
  const buildCompactProps = useCallback(
    (conversation: ConversationDetail, idx: number) => ({
      index: idx,
      conversation: conversation,
      api: api,
      isViewCard: false,
      forceShowDetails: showAllDetails,
      hasArchivedFilter: hasArchivedFilter,
      isConversationDetailsView: false,
      storageData: storageData,
      configMenus: configMenus,
      config: config,
      realItemTypes: realItemTypes,
      userData: userData,
      columnWidth: columnWidth,
      hideProjectName: hideProjectName,
      HasItemCreationPermission: hasItemCreationPermission,
      ShowMoveto: true,
      groupIsDefault: group.IsDefault,
      SocketConnectionId: socketConnectionId,
      corgeeFunctions: {
        onSendError: () => {},
        loadingBarStart: () => {},
        loadingBarComplete: () => {},
        callBackUpdateComplete: (response: any) =>
        {
          if (onCallBackUpdateComplete) onCallBackUpdateComplete(response);
        },
        openInfoWindow: (conv: ConversationDetail) =>
        {
          handleConversationViewed(conv.ConversationId);
          if (onOpenInfoWindow) onOpenInfoWindow(conv);
        },
        onCloseInfoWindow: (conv: ConversationDetail) =>
        {
          if (onOpenInfoWindow) onOpenInfoWindow(conv);
        },
        editConversation: (e: any, conv: ConversationDetail) =>
        {
          if (onEditConversation) onEditConversation(e, conv);
        },
        createConversation: (conv: ConversationDetail, cardType: SideMenuItem) =>
        {
          if (onCreateConversation) onCreateConversation(conv, cardType);
        },
        deleteConversation: (_e: any, conv: ConversationDetail) =>
        {
          if (onDeleteConversation) onDeleteConversation(conv.ConversationId);
        },
        archiveConversation: (_e: any, conv: ConversationDetail) =>
        {
          if (onArchiveConversation) onArchiveConversation(conv.ConversationId);
        },
        restoreConversation: (conv: ConversationDetail) =>
        {
          if (onRestoreConversation) onRestoreConversation(conv.ConversationId);
        },
        deleteConversationPermanently: (conv: ConversationDetail) =>
        {
          if (onDeleteConversationPermanently) onDeleteConversationPermanently(conv.ConversationId);
        },
        onDuplicateItem: (e: any, conv: ConversationDetail) =>
        {
          if (onDuplicateItem) onDuplicateItem(e, conv);
        },
        onRemoveFromColumn: (e: any, conv: ConversationDetail) =>
        {
          if (onRemoveFromColumn) onRemoveFromColumn(e, conv);
        },
        onBulkRemoveFromColumn: (conversations: ConversationDetail[]) =>
        {
          if (onBulkRemoveFromColumn) onBulkRemoveFromColumn(conversations);
        },
        bulkDeleteConversations: (conversationIds: number[]) =>
        {
          if (onBulkDeleteConversations) onBulkDeleteConversations(conversationIds);
        },
        onConversationsProjectChanged: (
          changes: Array<{ ConversationId: number; ProjectId: number; ProjectName: string }>,
        ) =>
        {
          if (onConversationsProjectChanged) onConversationsProjectChanged(changes);
        },
        onChangedProject: (_eOrId: any, conv?: any) =>
        {
          const conversationId = typeof _eOrId === 'number' ? _eOrId : conv?.ConversationId;
          if (onChangedProject && conversationId) onChangedProject(conversationId);
        },
        onMoveFromColumn: (
          e: any,
          conv: ConversationDetail,
          newGroupId: number,
          _response?: Dashboard,
        ) =>
        {
          if (onMoveFromColumn) onMoveFromColumn(e, conv, newGroupId);
        },
        addToColumn: (conv: ConversationDetail, targetGroupId: number) =>
        {
          if (onAddToColumn) onAddToColumn(conv, targetGroupId);
        },
        goToProject: (e: any, conv: ConversationDetail) =>
        {
          const path = `/ProjectDetails/${conv.ProjectId}`;
          if (e && (e.shiftKey || e.ctrlKey || e.metaKey))
          {
            window.open(window.location.origin + path, '_blank');
          }
          else
          {
            navigate(path);
          }
        },
      },
      onUpdateConversation: (convId: number, updates: Partial<ConversationDetail>) =>
      {
        if (onUpdateConversation) onUpdateConversation(convId, updates);
      },
      onUpdateReply: (convId: number, replyId: number, updates: Partial<any>) =>
      {
        if (!onUpdateConversation) return;
        const updatedReplies = (conversation.Replies || []).map((r: any) =>
          r.ReplyId === replyId ? { ...r, ...updates } : r,
        );
        onUpdateConversation(convId, { Replies: updatedReplies } as any);
      },
      onAddLayerToCard: (e: any, layer: any, functions?: any) =>
        handleAddLayerToCard(conversation.ConversationId, e, layer, functions),
      onAddLayerToProject: (e: any, layer: any, functions?: any) =>
        handleAddLayerToProject(conversation.ConversationId, e, layer, functions),
    }),
    [
      api,
      config,
      userData,
      storageData,
      configMenus,
      realItemTypes,
      onOpenInfoWindow,
      onUpdateConversation,
      onDeleteConversation,
      onArchiveConversation,
      onRestoreConversation,
      onDeleteConversationPermanently,
      columnWidth,
      hideProjectName,
      hasItemCreationPermission,
      showAllDetails,
      onEditConversation,
      onCreateConversation,
      onRemoveFromColumn,
      onBulkRemoveFromColumn,
      onBulkDeleteConversations,
      onConversationsProjectChanged,
      onChangedProject,
      onMoveFromColumn,
      onAddToColumn,
      onCallBackUpdateComplete,
      handleConversationViewed,
      socketConnectionId,
      handleAddLayerToCard,
      handleAddLayerToProject,
    ],
  );

  // Filter out child conversations from flat list (only show root-level cards)
  const rootConversations = useMemo(
    () =>
      group.Conversations
        ? group.Conversations.filter(c => !c.ParentConversationId || c.ParentConversationId === 0)
        : [],
    [group.Conversations],
  );

  // Memoized list of card IDs for SortableContext (root cards only)
  // const cardIds = useMemo(
  //   () => rootConversations.map((conv) => `card-${conv.ConversationId}`),
  //   [rootConversations]
  // );

  // Pre-build all card props to avoid creating new objects in render loop
  const memoizedCardProps = useMemo(() =>
  {
    if (!group.Conversations) return {};

    const propsMap: Record<number, any> = {};
    group.Conversations.forEach((conv, idx) =>
    {
      const base =
        cardViewMode === 'detailed' ? buildViewCardProps(conv, idx) : buildCompactProps(conv, idx);
      propsMap[conv.ConversationId] = {
        ...base,
        hasUnreadNotification: unreadConversationIds.has(conv.ConversationId),
      };
    });
    return propsMap;
  }, [
    group.Conversations,
    cardViewMode,
    buildViewCardProps,
    buildCompactProps,
    columnWidth,
    unreadConversationIds,
  ]);

  // Ref for the scrollable container (used by virtualizer)
  const parentRef = useRef<HTMLDivElement>(null);

  // Stable callback ref to combine react-dnd dropCards and parentRef
  const contentRef = useCallback(
    (node: HTMLDivElement | null) =>
    {
      dropCards(node);
      (parentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [dropCards],
  );

  // Stable refs for infinite scroll handler (avoids re-registering on every render)
  const onLoadMoreCardsRef = useRef(onLoadMoreCards);
  const hasMoreCardsRef = useRef(hasMoreCards);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const groupIdRef = useRef(group.DashboardGroupId);
  const conversationsLengthRef = useRef(group.Conversations?.length || 0);
  onLoadMoreCardsRef.current = onLoadMoreCards;
  hasMoreCardsRef.current = hasMoreCards;
  isLoadingMoreRef.current = isLoadingMore;
  groupIdRef.current = group.DashboardGroupId;
  conversationsLengthRef.current = group.Conversations?.length || 0;

  // Scroll-to-bottom detection for infinite scroll pagination
  useEffect(() =>
  {
    const el = parentRef.current;
    if (!el) return;

    const handleScroll = () =>
    {
      const { scrollTop, scrollHeight, clientHeight } = el;

      // Infinite scroll: load more cards when near bottom
      if (onLoadMoreCardsRef.current && hasMoreCardsRef.current && !isLoadingMoreRef.current)
      {
        if (scrollHeight - scrollTop - clientHeight < 150)
        {
          onLoadMoreCardsRef.current(groupIdRef.current);
          hasLoadedMoreRef.current = true;
        }
      }

      // Show "No More Items" message when user is near bottom and no more cards to load
      if (!hasMoreCardsRef.current && conversationsLengthRef.current > 0)
      {
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;
        setShouldShowNoMoreItems(hasLoadedMoreRef.current || isNearBottom);
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []); // Attach once on mount

  // Check if we should show "No More Items" message
  // When no scroll (content fits viewport): show immediately if !hasMoreCards
  // When scroll exists: show only when user has scrolled near bottom or loaded more
  useEffect(() =>
  {
    const conversations = group.Conversations || [];
    if (!hasMoreCards && conversations.length > 0)
    {
      // Wait for virtualizer to render and DOM to update
      const timeoutId = setTimeout(() =>
      {
        const el = parentRef.current;
        if (!el) return;
        const { scrollHeight, clientHeight, scrollTop } = el;
        const hasScroll = scrollHeight > clientHeight;

        if (!hasScroll)
        {
          // No scroll: show message immediately
          setShouldShowNoMoreItems(true);
        }
        else
        {
          // Has scroll: show only if user has scrolled near bottom or loaded more pages
          const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;
          setShouldShowNoMoreItems(hasLoadedMoreRef.current || isNearBottom);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    return () =>
    {
      setShouldShowNoMoreItems(false);
    };
  }, [hasMoreCards, group.Conversations, isLoadingMore]);

  // Virtualizer for rendering only visible items (root cards only)
  // getItemKey maps by ConversationId so measurements stay correct when items are removed/reordered
  const virtualizer = useVirtualizer({
    count: rootConversations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () =>
      cardViewMode === 'detailed' && showAllDetails
        ? 500
        : cardViewMode === 'detailed'
          ? 200
          : showAllDetails
            ? 350
            : 80,
    overscan: showAllDetails ? 15 : 5,
    getItemKey: index => rootConversations[index]?.ConversationId ?? index,
  });

  // When showAllDetails or cardViewMode toggles, force the virtualizer to re-measure all items
  // so that newly expanded/collapsed cards don't push later items out of the render window
  useEffect(() =>
  {
    virtualizer.measure();
  }, [showAllDetails, cardViewMode]);

  const isColumnHighlighted = pendingGroupIds?.has(group.DashboardGroupId) ?? false;

  return (
    <div
      className="flex-shrink-0 rounded-lg h-full flex flex-col"
      style={{
        width: `${columnWidth}px`,
        ...style,
      }}
      ref={columnRef}
    >
      {/* Column Header */}
      <div
        className="px-3 border-gray-300 dark:border-[#4a4c4c] flex items-center justify-between"
        ref={headerRef}
        style={{
          backgroundColor: isColumnHighlighted ? '#ffffe1' : 'transparent',
          transition: 'background-color 0.4s ease',
          cursor: 'grab',
          height: '38px',
          flexShrink: 0,
          overflow: 'hidden',
          paddingTop: '6px',
          paddingBottom: '6px',
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {group.IsDefault && <SwapVert htmlColor="#62aacb" />}
          {isEditingName ? (
            <div className="flex items-center min-w-0 flex-1 mr-2">
              <input
                ref={inputRef}
                type="text"
                value={columnName}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-[#4a4c4c] focus:outline-none bg-white dark:bg-[#3a3c3c] dark:text-[#a9a093]"
                autoFocus
                onChange={e => setColumnName(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={e => e.target.select()}
                onBlur={saveName}
              />
              <button
                onClick={saveName}
                style={{ backgroundColor: '#62aacb' }}
                className="text-white text-xs px-2 py-1 font-bold whitespace-nowrap"
              >
                Save
              </button>
            </div>
          ) : (
            <h3
              className="text-sm font-bold text-gray-500 dark:text-[#a9a093] truncate min-w-0"
              onDoubleClick={() => setIsEditingName(true)}
            >
              {columnName}
            </h3>
          )}
        </div>

        <div className="flex items-center gap-0 flex-shrink-0">
          {!recycleBin &&
            !group.IsDefault &&
            rootCardCount > 0 && (
            <span className="text-gray-400 dark:text-[#6e6860] text-xs leading-none mr-2 mb-1">
                ({rootCardCount})
            </span>
          )}
          {!recycleBin && configValueId === 15 && CorgeeStorageHelper.isAdmin() && (
            <button
              className="enable-help-tooltip flex items-center justify-center"
              ht-name="Add People"
              title="Add People"
              onClick={e =>
              {
                e.stopPropagation();
                navigate('/AddPeople');
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
            >
              <PersonAdd style={{ fontSize: 20, color: '#666' }} />
            </button>
          )}
          {!recycleBin && hasItemCreationPermission && (
            <button
              className="dashboard-groups-add-column enable-help-tooltip"
              ht-name="Add New Card"
              onClick={e =>
              {
                e.stopPropagation();
                if (onAddNewTask) onAddNewTask(e, group);
              }}
            />
          )}
          {/* Kebab menu */}
          {!recycleBin && (
            <div className="relative" ref={menuRef}>
              <button
                ref={kebabBtnRef}
                className="dg-kebab-btn"
                onClick={e =>
                {
                  e.stopPropagation();
                  if (!menuOpen && kebabBtnRef.current)
                  {
                    const rect = kebabBtnRef.current.getBoundingClientRect();
                    const MENU_WIDTH = 210;
                    const left =
                      window.innerWidth - rect.right >= MENU_WIDTH + 4
                        ? rect.right + 4
                        : rect.left - MENU_WIDTH - 4;
                    setMenuPos({ top: rect.top, left });
                  }
                  setMenuOpen(prev => !prev);
                }}
              >
                <span className="dg-kebab-dot" />
                <span className="dg-kebab-dot" />
                <span className="dg-kebab-dot" />
              </button>
              {menuOpen && (
                <RenderInBody>
                  <div className="dg-column-menu" style={{ top: menuPos.top, left: menuPos.left }}>
                    <button
                      className="dg-column-menu-item"
                      onClick={() => handleMenuAction('edit')}
                    >
                      <span className="dg-menu-icon">
                        <span className="icon-pencil-icon"></span>
                      </span>
                      <span>Edit Column</span>
                    </button>
                    <button
                      className="dg-column-menu-item"
                      onClick={() => handleMenuAction('delete')}
                    >
                      <span className="dg-menu-icon">
                        <span className="icon-trash-icon"></span>
                      </span>
                      <span>Delete Column</span>
                    </button>
                    {!group.IsDefault && (
                      <>
                        <button
                          className="dg-column-menu-item"
                          onClick={() => handleMenuAction('moveTo')}
                        >
                          <span className="dg-menu-icon">
                            <span className="icon-share-icon"></span>
                          </span>
                          <span>Move To</span>
                        </button>
                        <button
                          className="dg-column-menu-item"
                          onClick={() => handleMenuAction('copyTo')}
                        >
                          <span className="dg-menu-icon">
                            <span className="fa fa-clone"></span>
                          </span>
                          <span>Copy To</span>
                        </button>
                        <button
                          className="dg-column-menu-item"
                          onClick={() => handleMenuAction('makeDynamic')}
                        >
                          <span className="dg-menu-icon">
                            <span className="material-icons">import_export</span>
                          </span>
                          <span>Make This Column Dynamic</span>
                        </button>
                      </>
                    )}
                    <button
                      className="dg-column-menu-item"
                      onClick={() => handleMenuAction('showAllDetails')}
                    >
                      <span className="dg-menu-icon">
                        <span className={showAllDetails ? 'fa fa-check-square-o' : 'icons-corgee-show-details'}></span>
                      </span>
                      <span>{showAllDetails ? 'Hide All Details' : 'Show All Details'}</span>
                    </button>
                    <button
                      className="dg-column-menu-item"
                      onClick={() => handleMenuAction('selectAll')}
                    >
                      <span className="dg-menu-icon">
                        <span className="icons-menu-collections"></span>
                      </span>
                      <span>Select All</span>
                    </button>
                  </div>
                </RenderInBody>
              )}
              {menuOpen && (
                <RenderInBody>
                  <div className="dg-column-menu-backdrop" onClick={() => setMenuOpen(false)} />
                </RenderInBody>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content - Virtualized */}
      <div
        className={`flex-1 overflow-y-auto p-3 border-gray-300 dark:border-[#4a4c4c] dashboardGroupSelector ${isOverCards || isInfoWindowDragOver ? 'bg-blue-50 dark:bg-[#1e3a4f]' : ''}`}
        ref={contentRef}
        onDragOver={handleNativeDragOver}
        onDragLeave={handleNativeDragLeave}
        onDrop={handleNativeDrop}
      >
        {isAddingTask && onTaskAdded && onCancelAddTask && (
          <AddInlineTask
            groupId={group.DashboardGroupId}
            dashboardId={group.DashboardId}
            configValueId={configValueId || 0}
            projectId={projectId ? parseInt(projectId, 10) : undefined}
            projectName={projectName}
            onAdd={onTaskAdded}
            onCancel={onCancelAddTask}
            api={api}
            config={config}
            userData={userData}
          />
        )}

        {rootConversations.length === 0 ? (
          <div className="dashboard-group-no-more-items">No More Items</div>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map(virtualRow =>
            {
              const conv = rootConversations[virtualRow.index];
              if (!conv) return null;
              const isParent = conv.IsParent === true;
              const isExpanded = isParent && expandedParentIds?.has(conv.ConversationId);
              const childConvs = isExpanded
                ? (() =>
                {
                  const descendantIds = getAllDescendantIds(
                    group.Conversations,
                    conv.ConversationId,
                  );
                  return group.Conversations.filter(c => descendantIds.has(c.ConversationId));
                })()
                : [];
              return (
                <div
                  key={conv.ConversationId}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: '6px',
                  }}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className={cardsDisabled ? 'conversation-disabled' : undefined}
                >
                  <SortableCard
                    conversation={conv}
                    cardViewMode={cardViewMode}
                    cardProps={memoizedCardProps[conv.ConversationId]}
                    idx={virtualRow.index}
                    groupId={group.DashboardGroupId}
                    dashboardId={dashboardId}
                    isSelected={selectedCardIds.has(conv.ConversationId)}
                    isMultiDragActive={isMultiDragActive}
                    onCtrlClick={onCardCtrlClick}
                    onShiftClick={onCardShiftClick}
                    isHighlighted={pendingConversationIds?.has(conv.ConversationId) ?? false}
                    selectedCount={selectedCardIds.size}
                    getSelectedConversations={getSelectedConversations}
                    isParent={isParent}
                    isExpanded={!!isExpanded}
                    onToggleExpand={onToggleExpandParent}
                    onMakeChild={onMakeChild}
                    hierarchyDropActive={hierarchyDropActive}
                    onCardDrop={onCardDrop}
                    recycleBin={recycleBin}
                    isGroupDefault={group.IsDefault}
                  />
                  {/* Expanded children section (react-nestable) */}
                  {isExpanded && childConvs.length > 0 && (
                    <ExpandedChildren
                      parentId={conv.ConversationId}
                      groupId={group.DashboardGroupId}
                      children={childConvs}
                      maxDepth={10}
                      cardViewMode={cardViewMode}
                      columnWidth={columnWidth}
                      onNestableChange={onNestableChange}
                      api={api}
                      config={config}
                      storageData={storageData}
                      realItemTypes={realItemTypes}
                      configMenus={configMenus}
                      userData={userData}
                      hideProjectName={hideProjectName}
                      onOpenInfoWindow={onOpenInfoWindow}
                      onEditConversation={onEditConversation}
                      onCreateConversation={onCreateConversation}
                      onUpdateConversation={onUpdateConversation}
                      onDeleteConversation={onDeleteConversation}
                      onArchiveConversation={onArchiveConversation}
                      onRestoreConversation={onRestoreConversation}
                      onDeleteConversationPermanently={onDeleteConversationPermanently}
                      onDuplicateItem={onDuplicateItem}
                      expandedParentIds={expandedParentIds}
                      onToggleExpandParent={onToggleExpandParent}
                      onMakeChild={onMakeChild}
                      isGroupDefault={group.IsDefault}
                      selectedCardIds={selectedCardIds}
                      isMultiDragActive={isMultiDragActive}
                      onCardCtrlClick={onCardCtrlClick}
                      onCardShiftClick={onCardShiftClick}
                      getSelectedConversations={getSelectedConversations}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Infinite scroll loading indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-3">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: '#62aacb', borderTopColor: 'transparent' }}
            />
          </div>
        )}

        {/* No More Items message */}
        {shouldShowNoMoreItems && !isLoadingMore && rootConversations.length > 0 && (
          <div className="dashboard-group-no-more-items">No More Items</div>
        )}
      </div>

      {/* Modals */}
      {openDeleteColumnModal && (
        <RenderInBody>
          <DeleteDashboardGroupModal
            dashboardGroupId={group.DashboardGroupId}
            open={openDeleteColumnModal}
            setOpen={setOpenDeleteColumnModal}
            onUpdateDashboardGroupPosition={onUpdateDashboardGroupPosition}
          />
        </RenderInBody>
      )}
      {openMoveToColumnModal && (
        <RenderInBody>
          <MoveCopyToDashboardGroupModal
            open={openMoveToColumnModal}
            setOpen={setOpenMoveToColumnModal}
            dashboardGroupOrigin={group}
            isMoveTo={true}
            projectId={projectId}
            onUpdateDashboardGroupPosition={onUpdateDashboardGroupPosition || (() => {})}
            configValueId={configValueId || 1}
          />
        </RenderInBody>
      )}
      {openCopyToColumnModal && (
        <RenderInBody>
          <MoveCopyToDashboardGroupModal
            open={openCopyToColumnModal}
            setOpen={setOpenCopyToColumnModal}
            dashboardGroupOrigin={group}
            isMoveTo={false}
            projectId={projectId}
            onUpdateDashboardGroupPosition={onUpdateDashboardGroupPosition || (() => {})}
            configValueId={configValueId || 1}
          />
        </RenderInBody>
      )}
    </div>
  );
}, areGroupPropsEqual);

// Sortable Card wrapper component - MEMOIZED for performance
interface SortableCardProps {
  conversation: ConversationDetail;
  cardViewMode: CardViewMode;
  cardProps: any; // Pre-built props to avoid rebuilding on each render
  idx: number;
  groupId: number;
  dashboardId?: number;
  isSelected: boolean;
  isMultiDragActive: boolean;
  onCtrlClick: (cardId: number, shiftKey: boolean, groupId: number) => void;
  onShiftClick?: (cardId: number, groupId: number) => void;
  isHighlighted?: boolean;
  /** Total number of currently selected cards (used to compute isMultiple for the action menu) */
  selectedCount: number;
  /** Stable callback that returns the full list of selected conversations */
  getSelectedConversations: () => ConversationDetail[];
  // Hierarchy props
  isParent?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (conversationId: number, groupId: number) => void;
  onMakeChild?: (
    childConversationId: number,
    parentConversationId: number,
    groupId: number,
    sourceGroupId?: number
  ) => void;
  hierarchyDropActive?: boolean;
  onCardDrop?: (
    draggedCardId: number,
    sourceGroupId: number,
    targetGroupId: number,
    overCardId: number | null
  ) => void;
  recycleBin?: boolean;
  isGroupDefault?: boolean;
}

// Custom comparison function for React.memo
// Only re-render if the actual card data or view mode changed
const areCardPropsEqual = (prevProps: SortableCardProps, nextProps: SortableCardProps): boolean =>
{
  // Always re-render if conversation data changed
  if (prevProps.conversation.ConversationId !== nextProps.conversation.ConversationId)
  {
    return false;
  }
  // Re-render if view mode changed
  if (prevProps.cardViewMode !== nextProps.cardViewMode)
  {
    return false;
  }
  // Re-render if columnWidth changed (for hiding/showing ProjectName)
  if (prevProps.cardProps?.columnWidth !== nextProps.cardProps?.columnWidth)
  {
    return false;
  }
  // Re-render if Show All Details toggled
  if (prevProps.cardProps?.forceShowDetails !== nextProps.cardProps?.forceShowDetails)
  {
    return false;
  }
  // Re-render if unread notification badge changed
  if (prevProps.cardProps?.hasUnreadNotification !== nextProps.cardProps?.hasUnreadNotification)
  {
    return false;
  }
  // Re-render if the conversation was updated (check key fields)
  if (
    prevProps.conversation.Title !== nextProps.conversation.Title ||
    prevProps.conversation.UpdatedAt !== nextProps.conversation.UpdatedAt ||
    prevProps.conversation.TotalReplies !== nextProps.conversation.TotalReplies ||
    prevProps.conversation.Replies !== nextProps.conversation.Replies ||
    prevProps.conversation.Finished !== nextProps.conversation.Finished ||
    prevProps.conversation.IsRedFlag !== nextProps.conversation.IsRedFlag ||
    prevProps.conversation.IsImportant !== nextProps.conversation.IsImportant ||
    prevProps.conversation.Progress !== nextProps.conversation.Progress ||
    prevProps.conversation.IsEnable !== nextProps.conversation.IsEnable ||
    prevProps.conversation.Icon !== nextProps.conversation.Icon ||
    prevProps.conversation.ConfigValueId !== nextProps.conversation.ConfigValueId ||
    prevProps.conversation.SideMenuText !== nextProps.conversation.SideMenuText ||
    prevProps.conversation._convertedAt !== nextProps.conversation._convertedAt ||
    prevProps.conversation.ProjectId !== nextProps.conversation.ProjectId ||
    prevProps.conversation.ProjectName !== nextProps.conversation.ProjectName ||
    prevProps.conversation.Reactions !== nextProps.conversation.Reactions ||
    prevProps.conversation.ConversationLayers !== nextProps.conversation.ConversationLayers
  )
  {
    return false;
  }
  // Re-render if selection or multi-drag state changed
  if (prevProps.isSelected !== nextProps.isSelected)
  {
    return false;
  }
  if (prevProps.selectedCount !== nextProps.selectedCount)
  {
    return false;
  }
  if (prevProps.isMultiDragActive !== nextProps.isMultiDragActive)
  {
    return false;
  }
  if (prevProps.isHighlighted !== nextProps.isHighlighted)
  {
    return false;
  }
  // Re-render if hierarchy state changed
  if (prevProps.isParent !== nextProps.isParent)
  {
    return false;
  }
  if (prevProps.isExpanded !== nextProps.isExpanded)
  {
    return false;
  }
  if (prevProps.hierarchyDropActive !== nextProps.hierarchyDropActive)
  {
    return false;
  }
  // Group/index changes don't need re-render of card content
  return true;
};

const SortableCard: React.FC<SortableCardProps> = memo(function SortableCard({
  conversation,
  cardViewMode, // used in areCardPropsEqual
  cardProps,
  idx: _idx, // used for identification in areCardPropsEqual
  groupId,
  dashboardId = 0,
  isSelected,
  isMultiDragActive,
  onCtrlClick,
  onShiftClick,
  isHighlighted,
  selectedCount,
  getSelectedConversations,
  isParent,
  isExpanded,
  onToggleExpand,
  onMakeChild,
  hierarchyDropActive,
  onCardDrop,
  recycleBin = false,
  isGroupDefault = false,
})
{
  // react-dnd: Card drag
  const cardRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag<CardDragItem, void, { isDragging: boolean }>({
    type: ItemTypes.CARD,
    item: () =>
    {
      setCardBeingDragged({
        conversationId: conversation.ConversationId,
        groupId: groupId ?? 0,
        dashboardId,
        configValueId: conversation.ConfigValueId != null ? Number(conversation.ConfigValueId) : undefined,
      });
      window.dispatchEvent(
        new CustomEvent('corgee:card-drag-start', {
          detail: { conversationId: conversation.ConversationId, groupId, dashboardId },
        }),
      );
      return {
        type: ItemTypes.CARD,
        id: conversation.ConversationId,
        columnId: groupId ?? 0,
        dashboardId,
      };
    },
    end: () =>
    {
      setCardBeingDragged(null);
      window.dispatchEvent(new CustomEvent('corgee:card-drag-end'));
    },
    // Allow both move and copy so the sidebar drop targets can show the correct cursor
    // (dropEffect='move' = no '+', dropEffect='copy' = '+' icon when Ctrl held)
    options: { dropEffect: 'copyMove' },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => !recycleBin && !document.activeElement?.closest('.note-editable'),
  });

  // Suppress the native browser ghost when multi-drag is active so only
  // our custom portal overlay (in DashboardGroups) is visible.
  if (isSelected && (selectedCount ?? 0) > 1)
  {
    preview(getEmptyImage());
  }

  // Track whether the cursor is in the bottom 35% of the card (make-child zone).
  // Computed in `hover` (not `collect`) to keep getBoundingClientRect out of the
  // render cycle — calling it inside `collect` caused "Maximum update depth exceeded".
  const isInChildZoneRef = useRef(false);
  const [isInChildZone, setIsInChildZone] = useState(false);

  const NO_NEST_CONFIG_VALUES = [9, 15];

  const [{ isOverCard }, drop] = useDrop<CardDragItem, void, { isOverCard: boolean }>({
    accept: ItemTypes.CARD,
    collect: monitor => ({
      isOverCard: monitor.isOver({ shallow: true }),
    }),
    canDrop: item =>
    {
      if (recycleBin || item.id === conversation.ConversationId) return false;
      if (isGroupDefault) return false;
      const dragged = getCardBeingDragged();
      const draggedConfigValueId = dragged?.configValueId ?? 0;
      if (
        NO_NEST_CONFIG_VALUES.includes(draggedConfigValueId) ||
        NO_NEST_CONFIG_VALUES.includes(Number(conversation.ConfigValueId ?? 0))
      )
        return false;
      return true;
    },
    hover: (_item, monitor) =>
    {
      const offset = monitor.getClientOffset();
      const el = cardRef.current;
      if (!offset || !el) return;
      const { top, height } = el.getBoundingClientRect();
      const newIsBottom = offset.y - top > height * 0.7;
      if (newIsBottom !== isInChildZoneRef.current)
      {
        isInChildZoneRef.current = newIsBottom;
        setIsInChildZone(newIsBottom);
      }
    },
    drop: (item, monitor) =>
    {
      if (monitor.didDrop()) return;
      if (item.id === conversation.ConversationId) return;
      const offset = monitor.getClientOffset();
      const el = cardRef.current;
      const isBottom =
        !!offset && !!el
          ? offset.y - el.getBoundingClientRect().top > el.getBoundingClientRect().height * 0.7
          : isInChildZoneRef.current;
      const dragged = getCardBeingDragged();
      const draggedConfigValueId = dragged?.configValueId ?? 0;
      const nestingAllowed =
        !isGroupDefault &&
        !NO_NEST_CONFIG_VALUES.includes(draggedConfigValueId) &&
        !NO_NEST_CONFIG_VALUES.includes(Number(conversation.ConfigValueId ?? 0));
      if (isBottom && nestingAllowed)
      {
        onMakeChild?.(item.id, conversation.ConversationId, groupId ?? 0, item.columnId);
      }
      else
      {
        onCardDrop?.(item.id, item.columnId, groupId ?? 0, conversation.ConversationId);
      }
    },
  });

  // Single ref for both drag source and drop target (same pattern as DndTestPage)
  drag(drop(cardRef));

  // Reset child-zone indicator when the cursor leaves this card
  useEffect(() =>
  {
    if (!isOverCard)
    {
      isInChildZoneRef.current = false;
      setIsInChildZone(false);
    }
  }, [isOverCard]);

  const draggedForVisual = getCardBeingDragged();
  const nestingBlocked =
    isGroupDefault ||
    NO_NEST_CONFIG_VALUES.includes(draggedForVisual?.configValueId ?? 0) ||
    NO_NEST_CONFIG_VALUES.includes(Number(conversation.ConfigValueId ?? 0));
  const isOverChildZone = isOverCard && isInChildZone && !nestingBlocked;
  const isOverCardEffective = isOverCard && !nestingBlocked;
  const showPlaceholder = isDragging;
  // Collapse other selected cards during multi-drag (so only 1 placeholder is visible)
  const isHiddenMultiDrag = !isDragging && isSelected && isMultiDragActive;

  const style: React.CSSProperties = useMemo(
    () => ({
      opacity: isDragging ? 0.4 : undefined,
      zIndex: isDragging ? 1 : undefined,
    }),
    [isDragging],
  );

  // Stable refs for the callback values
  const onCtrlClickRef = useRef(onCtrlClick);
  const onShiftClickRef = useRef(onShiftClick);
  const conversationIdRef = useRef(conversation.ConversationId);
  onCtrlClickRef.current = onCtrlClick;
  onShiftClickRef.current = onShiftClick;
  conversationIdRef.current = conversation.ConversationId;

  // Register this card's DOM element in the global registry (used for multi-drag ghost)
  useEffect(() =>
  {
    const el = cardRef.current;
    if (!el) return;
    registerCardElement(conversation.ConversationId, el);
    return () => registerCardElement(conversation.ConversationId, null);
  }, [conversation.ConversationId]);

  // Attach native listeners in capture phase for Ctrl+Click selection.
  // pointerdown: triggers the selection toggle.
  // click: suppresses any click handler inside the card (e.g. title → InfoWindow)
  //        because preventDefault on pointerdown does NOT prevent the click event.
  useEffect(() =>
  {
    const el = cardRef.current;
    if (!el) return;

    const handlePointerDown = (e: PointerEvent) =>
    {
      if (e.ctrlKey || e.metaKey)
      {
        e.preventDefault();
        e.stopImmediatePropagation();
        onCtrlClickRef.current?.(conversationIdRef.current, e.shiftKey, groupId ?? 0);
      }
      else if (e.shiftKey)
      {
        e.preventDefault();
        e.stopImmediatePropagation();
        onShiftClickRef.current?.(conversationIdRef.current, groupId ?? 0);
      }
    };

    const handleClick = (e: MouseEvent) =>
    {
      if (e.ctrlKey || e.metaKey || e.shiftKey)
      {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    el.addEventListener('pointerdown', handlePointerDown, true);
    el.addEventListener('click', handleClick, true);
    return () =>
    {
      el.removeEventListener('pointerdown', handlePointerDown, true);
      el.removeEventListener('click', handleClick, true);
    };
  }, []);

  // Handle expand/collapse click
  const handleExpandClick = useCallback(
    (e: React.MouseEvent) =>
    {
      e.stopPropagation();
      e.preventDefault();
      onToggleExpand?.(conversation.ConversationId, groupId ?? 0);
    },
    [onToggleExpand, conversation.ConversationId, groupId],
  );

  const wrapperClass = showPlaceholder
    ? 'dg-card-placeholder'
    : isDragging && hierarchyDropActive
      ? 'dg-card-multi-hidden'
      : isHiddenMultiDrag
        ? 'dg-card-multi-hidden'
        : isSelected
          ? 'dg-card-selected'
          : isHighlighted
            ? 'dg-card-highlight'
            : isOverChildZone
              ? 'dg-card-hierarchy-target'
              : isOverCardEffective
                ? 'dg-card-hover-target'
                : undefined;

  const detailedClass = cardViewMode === 'detailed' ? 'dg-card-detailed' : undefined;
  const combinedClass = [wrapperClass, detailedClass].filter(Boolean).join(' ') || undefined;

  return (
    <div
      ref={cardRef}
      style={style}
      className={combinedClass}
      data-conversationid={conversation.ConversationId}
    >
      {/* Expand/collapse icon for parent cards */}
      {isParent && (
        <button
          className={`dg-expand-toggle expandable-control${isExpanded ? ' expanded' : ''}`}
          onClick={handleExpandClick}
          onPointerDown={e => e.stopPropagation()}
          title={isExpanded ? 'Collapse children' : 'Expand children'}
        />
      )}
      <ul className="list-none p-0 m-0">
        {React.createElement(ConversationItem as any, {
          ...cardProps,
          isMultiple: isSelected && (selectedCount ?? 0) > 1,
          getSelectedConversations,
        })}
      </ul>
    </div>
  );
}, areCardPropsEqual);

export default DashboardGroupColumn;
